import { type ReactNode, useEffect } from 'react';

/**
 * Centred modal dialog shell — used for create flows.
 * Closes on Escape and on overlay click. Renders inline (no portal); the fixed
 * overlay is z-50 to clear the sticky header (z-10).
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="card w-full max-w-2xl shadow-xl"
        onClick={(e) => { e.stopPropagation(); }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-bg-line">
          <h2 className="text-sm font-mono text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink text-lg leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-bg-raised"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="px-5 py-4">
          {children}
        </div>
        {footer !== undefined && (
          <footer className="px-5 py-3 border-t border-bg-line flex items-center justify-end gap-2">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
